import React, { useState, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';
import {
  Box,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  PersonOutlined,
  EmailOutlined
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useUser } from 'hooks/useUser';
import { getUser } from 'graphql/queries';
import InitialsAvatar from './components/InitialsAvatar';

/**
 * Status color mapping for user status values
 */
const STATUS_COLORS = {
  active: 'success',
  inactive: 'default',
  pending: 'warning',
  suspended: 'error'
};

/**
 * InfoRow - Display a labeled piece of user information
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.label - Label text
 * @param {string} props.value - Value to display
 */
function InfoRow({ icon, label, value }) {
  return (
    <Box
      display="flex"
      alignItems="center"
      py={2}
      sx={{
        '&:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'divider'
        }
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{
          minWidth: 200,
          color: 'text.secondary'
        }}
      >
        {icon && (
          <Box
            display="flex"
            alignItems="center"
            mr={1.5}
            sx={{ color: '#7C3AED' }}
          >
            {icon}
          </Box>
        )}
        <Typography variant="body2" fontWeight="medium" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="body1" color="text.primary">
        {value || '—'}
      </Typography>
    </Box>
  );
}

/**
 * UserProfile - Self-service user profile page
 *
 * Displays read-only user information including:
 * - Dynamic initials avatar
 * - Personal info (name, email)
 * - Account status
 */
export default function UserProfile() {
  const { user: contextUser } = useUser();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchUserData() {
      try {
        setLoading(true);
        setError(null);

        // Get current user from Cognito
        const cognitoUser = await Auth.currentUserPoolUser();

        if (!cognitoUser) {
          throw new Error('No authenticated user found');
        }

        // If we have user in context, use it as initial data
        if (contextUser?.id) {
          setUserData(contextUser);

          // Fetch fresh data in background
          try {
            const response = await API.graphql({
              query: getUser,
              variables: { id: contextUser.id }
            });

            if (response.data?.getUser) {
              setUserData(response.data.getUser);
            }
          } catch (fetchError) {
            console.error('Error fetching fresh user data:', fetchError);
            // Keep using context user data
          }
        } else {
          throw new Error('User data not available');
        }
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError(err.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [contextUser?.id]);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3}>
          <Card>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
              p={3}
            >
              <CircularProgress />
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3}>
          <Card>
            <Box p={3}>
              <Typography variant="h6" color="error" gutterBottom>
                Error Loading Profile
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  // Main content
  const fullName = userData?.firstName && userData?.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : userData?.userName || 'User';

  const statusColor = STATUS_COLORS[userData?.status?.toLowerCase()] || 'default';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={3} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={10} lg={8}>
            <Card>
              <Box p={4}>
                {/* Header with Avatar */}
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                  mb={4}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    mb={{ xs: 2, sm: 0 }}
                    mr={{ sm: 3 }}
                  >
                    <InitialsAvatar user={userData} size="xl" />
                  </Box>
                  <Box
                    flex={1}
                    textAlign={{ xs: 'center', sm: 'left' }}
                  >
                    <Typography variant="h3" fontWeight="bold" gutterBottom>
                      {fullName}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      @{userData?.userName}
                    </Typography>
                    {userData?.status && (
                      <Chip
                        label={userData.status.charAt(0).toUpperCase() + userData.status.slice(1)}
                        color={statusColor}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Personal Information Section */}
                <Box>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Personal Information
                  </Typography>
                  <InfoRow
                    icon={<PersonOutlined />}
                    label="Full Name"
                    value={fullName}
                  />
                  <InfoRow
                    icon={<EmailOutlined />}
                    label="Email"
                    value={userData?.email}
                  />
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}
